import { useMutation, useQuery, useQueryClient } from "@tanstack/solid-query";
import { axiosInstance } from "@/api/client";
import {
  ATS_KEY,
  BILLING_USAGE_KEY,
  COMPANY_KEY,
  JOB_OFFER_KEY,
} from "@/api/query-keys";

import type { Asset, PaymentStatus } from "@/types";
import type {
  GetAllJobOffersParams,
  GetAllJobOffersResponse,
  GetAllProtectedJobOffersResponse,
  GetProtectedJobOfferResponse,
  JobOfferV2,
  JobOfferV2Update,
  JobOfferV2WithCompanyId,
} from "../types";

const jobOffersPerPage = 10;

export const getJobsOffers = async (
  params: GetAllJobOffersParams,
): Promise<GetAllJobOffersResponse> => {
  const { page, for_me, ...rest } = params;
  const queryParams: Record<string, unknown> = {
    ...rest,
  };

  // Convert for_me boolean to string "true" if it's true
  if (for_me === true) {
    queryParams.for_me = "true";
  }

  const { data } = await axiosInstance.get<GetAllJobOffersResponse>(
    `/job-offers?page=${page}&limit=${jobOffersPerPage}`,
    {
      params: queryParams,
    },
  );
  return data;
};

export const updateViewsCount = async (id: string) => {
  await axiosInstance.patch(`/job-offer/views/${id}`);
};

/**
 * La mutazione riceve l'id **al momento in cui parte**, come variabile, invece
 * di catturarne uno alla creazione.
 *
 * Presa alla creazione, l'id era congelato: un'istanza che attraversa più
 * offerte senza rimontare registrava la vista sulla prima. E siccome la vista
 * si registra quando l'offerta lascia lo schermo, prendere l'id «corrente» in
 * quel momento non basterebbe — sposterebbe soltanto l'errore sull'ultima.
 * Passarlo come variabile è ciò che permette a `useTrackJobOfferView` di
 * registrarne una per ciascuna, attribuita a quella giusta.
 */
export const useUpdateViewsCount = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: (id: string) => updateViewsCount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [JOB_OFFER_KEY] });
    },
  }));
};

export const getJobOfferV2 = async (
  id: string,
  payment_status?: PaymentStatus,
) => {
  const { data } = await axiosInstance.get<{
    job_offer: JobOfferV2WithCompanyId;
  }>(`/job-offer/${id}`, {
    params: {
      payment_status,
    },
  });
  return data;
};

export const createJobOfferQueryOptions = (
  id: string,
  payment_status?: PaymentStatus,
) => {
  return {
    queryKey: [JOB_OFFER_KEY, id],
    enabled: !!id,
    queryFn: () => getJobOfferV2(id, payment_status),
  };
};

export const useGetJobOfferV2 = (
  id: string,
  payment_status?: PaymentStatus,
) => {
  return useQuery(() => createJobOfferQueryOptions(id, payment_status));
};

const patchJobOffer = async (id: string, payload: JobOfferV2Update) => {
  const { data } = await axiosInstance.patch<{ job_offer: JobOfferV2 }>(
    `/job-offer/${id}`,
    {
      job_offer: payload,
    },
  );
  return data;
};

export const usePatchJobOffer = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: (payload: JobOfferV2Update) => patchJobOffer(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [JOB_OFFER_KEY] });
      queryClient.invalidateQueries({ queryKey: [COMPANY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ATS_KEY] });
      queryClient.invalidateQueries({ queryKey: [BILLING_USAGE_KEY] });
    },
  }));
};

const disableJobOffer = async ({
  id,
  isActive,
}: {
  id: string;
  isActive: boolean;
}) => {
  const { data } = await axiosInstance.patch<{ job_offer: JobOfferV2 }>(
    `/job-offer/${id}`,
    {
      job_offer: {
        is_active: isActive,
      },
    },
  );
  return data;
};

export const useDisableJobOfferV2 = () => {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      disableJobOffer({ id, isActive }),
    onSuccess: () => {
      queryClient.resetQueries({ queryKey: [JOB_OFFER_KEY] });
      queryClient.invalidateQueries({ queryKey: [COMPANY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ATS_KEY] });
      queryClient.invalidateQueries({ queryKey: [BILLING_USAGE_KEY] });
    },
  }));
};

// Protected endpoints - require authentication
export const getProtectedJobOffers = async (
  params: GetAllJobOffersParams,
): Promise<GetAllProtectedJobOffersResponse> => {
  const { page, ...rest } = params;
  const { data } = await axiosInstance.get<GetAllProtectedJobOffersResponse>(
    `/job-offers/protected?page=${page}&limit=${jobOffersPerPage}`,
    {
      params: rest,
    },
  );
  return data;
};

export const useGetProtectedJobOffers = (params: GetAllJobOffersParams) => {
  return useQuery(() => ({
    queryKey: [JOB_OFFER_KEY, "protected", params],
    enabled: !!params,
    queryFn: () => getProtectedJobOffers(params),
  }));
};

export const getProtectedJobOfferV2 = async (
  id: string,
  payment_status?: PaymentStatus,
) => {
  const { data } = await axiosInstance.get<GetProtectedJobOfferResponse>(
    `/job-offer/protected/${id}`,
    {
      params: {
        payment_status,
      },
    },
  );
  return data;
};

export const createProtectedJobOfferQueryOptions = (
  id: string,
  payment_status?: PaymentStatus,
) => {
  return {
    queryKey: [JOB_OFFER_KEY, "protected", id],
    enabled: !!id,
    queryFn: () => getProtectedJobOfferV2(id, payment_status),
  };
};

export const useGetProtectedJobOfferV2 = (
  id: string,
  payment_status?: PaymentStatus,
) => {
  return useQuery(() =>
    createProtectedJobOfferQueryOptions(id, payment_status),
  );
};

export const getAdditionalJobOfferDocuments = async (job_offer_id: string) => {
  const { data } = await axiosInstance.get<{
    assets: Asset[];
  }>(`/job-offer/${job_offer_id}/assets`);
  return data;
};

export const createAdditionalJobOfferDocumentsQueryOptions = (
  job_offer_id: string,
) => {
  return {
    queryKey: [JOB_OFFER_KEY, "additional-documents", job_offer_id],
    queryFn: () => getAdditionalJobOfferDocuments(job_offer_id),
  };
};
